import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Clock, 
  Plus, 
  Calendar,
  UserCheck,
  UserX,
  Timer,
  LogIn,
  LogOut,
  Trash2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  present: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  absent: 'bg-red-50 text-red-700 border-red-200',
  late: 'bg-amber-50 text-amber-700 border-amber-200',
  half_day: 'bg-blue-50 text-blue-700 border-blue-200',
  remote: 'bg-purple-50 text-purple-700 border-purple-200'
};

const Attendance = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  
  const [records, setRecords] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [formData, setFormData] = useState({});
  const [filterEmployee, setFilterEmployee] = useState('all');
  const [filterDate, setFilterDate] = useState('');

  const statusLabels = {
    present: t('pages.attendance.present'),
    absent: t('pages.attendance.absent'),
    late: t('pages.attendance.late'),
    half_day: t('pages.attendance.halfDay'),
    remote: t('pages.attendance.remote')
  };

  useEffect(() => {
    fetchRecords();
    fetchEmployees();
  }, []);

  const fetchRecords = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/hr/attendance`);
      setRecords(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/hr/employees`);
      setEmployees(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleSave = async () => {
    try {
      await axios.post(`${API_URL}/api/hr/attendance`, formData);
      toast.success(t('pages.attendance.attendanceRecorded'));
      setShowDialog(false);
      setFormData({});
      fetchRecords();
    } catch (err) {
      toast.error(t('pages.attendance.failedToRecord'));
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm(t('pages.attendance.confirmDelete'))) {
      try {
        await axios.delete(`${API_URL}/api/hr/attendance/${id}`);
        toast.success(t('pages.attendance.recordDeleted'));
        fetchRecords();
      } catch (err) {
        toast.error(t('pages.attendance.failedToDelete'));
      }
    }
  };

  const openNew = () => {
    setFormData({ status: 'present', date: format(new Date(), 'yyyy-MM-dd') });
    setShowDialog(true);
  };

  const getEmployeeName = (id) => {
    const emp = employees.find(e => e.id === id);
    return emp ? `${emp.first_name} ${emp.last_name}` : '-';
  };

  const filteredRecords = records
    .filter(r => filterEmployee === 'all' || r.employee_id === filterEmployee)
    .filter(r => !filterDate || r.date?.startsWith(filterDate));

  const stats = {
    present: records.filter(r => r.status === 'present').length,
    absent: records.filter(r => r.status === 'absent').length,
    late: records.filter(r => r.status === 'late').length,
    remote: records.filter(r => r.status === 'remote').length
  };

  return (
    <div className={`space-y-6 ${isRTL ? 'text-right' : ''}`}>
      <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t('pages.attendance.title')}</h1>
          <p className="text-muted-foreground">{t('pages.attendance.subtitle')}</p>
        </div>
        <Button onClick={openNew} className={`gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}><Plus className="h-4 w-4" /> {t('pages.attendance.recordAttendance')}</Button>
      </div>

      <div className={`grid grid-cols-4 gap-4 ${isRTL ? 'direction-rtl' : ''}`}>
        <Card><CardContent className="pt-6"><div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}><UserCheck className="h-8 w-8 text-emerald-500" /><div><div className="text-2xl font-bold">{stats.present}</div><div className="text-sm text-muted-foreground">{t('pages.attendance.present')}</div></div></div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}><UserX className="h-8 w-8 text-red-500" /><div><div className="text-2xl font-bold">{stats.absent}</div><div className="text-sm text-muted-foreground">{t('pages.attendance.absent')}</div></div></div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}><Clock className="h-8 w-8 text-amber-500" /><div><div className="text-2xl font-bold">{stats.late}</div><div className="text-sm text-muted-foreground">{t('pages.attendance.late')}</div></div></div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}><Calendar className="h-8 w-8 text-purple-500" /><div><div className="text-2xl font-bold">{stats.remote}</div><div className="text-sm text-muted-foreground">{t('pages.attendance.remote')}</div></div></div></CardContent></Card>
      </div>

      <div className={`flex items-center gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
        <Select value={filterEmployee} onValueChange={setFilterEmployee}>
          <SelectTrigger className="w-56"><SelectValue placeholder={t('pages.attendance.filterByEmployee')} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('pages.employees.allDepartments')}</SelectItem>
            {employees.map(e => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input type="date" className="w-40" value={filterDate} onChange={e => setFilterDate(e.target.value)} />
        {filterDate && <Button variant="ghost" size="sm" onClick={() => setFilterDate('')}>{t('pages.attendance.clear')}</Button>}
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className={isRTL ? 'text-right' : ''}>{t('pages.attendance.employee')}</TableHead>
              <TableHead className={isRTL ? 'text-right' : ''}>{t('pages.attendance.date')}</TableHead>
              <TableHead className={isRTL ? 'text-right' : ''}>{t('pages.attendance.checkIn')}</TableHead>
              <TableHead className={isRTL ? 'text-right' : ''}>{t('pages.attendance.checkOut')}</TableHead>
              <TableHead className={isRTL ? 'text-right' : ''}>{t('pages.attendance.hoursWorked')}</TableHead>
              <TableHead className={isRTL ? 'text-right' : ''}>{t('pages.attendance.status')}</TableHead>
              <TableHead className="w-[50px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredRecords.map((record) => (
              <TableRow key={record.id}>
                <TableCell className="font-medium">{getEmployeeName(record.employee_id)}</TableCell>
                <TableCell>{format(new Date(record.date), 'MMM d, yyyy')}</TableCell>
                <TableCell>
                  {record.check_in ? (
                    <span className={`flex items-center gap-1 text-emerald-600 ${isRTL ? 'flex-row-reverse' : ''}`}><LogIn className="h-3 w-3" /> {format(new Date(record.check_in), 'h:mm a')}</span>
                  ) : '-'}
                </TableCell>
                <TableCell>
                  {record.check_out ? (
                    <span className={`flex items-center gap-1 text-red-600 ${isRTL ? 'flex-row-reverse' : ''}`}><LogOut className="h-3 w-3" /> {format(new Date(record.check_out), 'h:mm a')}</span>
                  ) : '-'}
                </TableCell>
                <TableCell className="font-mono">{record.hours_worked?.toFixed(1) || '-'}h</TableCell>
                <TableCell>
                  <Badge className={statusColors[record.status]}>
                    {statusLabels[record.status] || record.status}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDelete(record.id)}>
                    <Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {filteredRecords.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="h-32 text-center">
                  <Clock className="h-8 w-8 mx-auto text-muted-foreground/30 mb-2" />
                  <p className="text-muted-foreground">{t('pages.attendance.noRecords')}</p>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{t('pages.attendance.recordAttendance')}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{t('pages.attendance.employee')} *</Label>
              <Select value={formData.employee_id || ''} onValueChange={v => setFormData({...formData, employee_id: v})}>
                <SelectTrigger><SelectValue placeholder={t('pages.attendance.selectEmployee')} /></SelectTrigger>
                <SelectContent>{employees.map(e => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('pages.attendance.date')} *</Label>
                <Input type="date" value={formData.date || ''} onChange={e => setFormData({...formData, date: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>{t('pages.attendance.status')}</Label>
                <Select value={formData.status || 'present'} onValueChange={v => setFormData({...formData, status: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="present">{t('pages.attendance.present')}</SelectItem>
                    <SelectItem value="absent">{t('pages.attendance.absent')}</SelectItem>
                    <SelectItem value="late">{t('pages.attendance.late')}</SelectItem>
                    <SelectItem value="half_day">{t('pages.attendance.halfDay')}</SelectItem>
                    <SelectItem value="remote">{t('pages.attendance.remote')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('pages.attendance.checkIn')}</Label>
                <Input type="datetime-local" value={formData.check_in || ''} onChange={e => setFormData({...formData, check_in: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>{t('pages.attendance.checkOut')}</Label>
                <Input type="datetime-local" value={formData.check_out || ''} onChange={e => setFormData({...formData, check_out: e.target.value})} />
              </div>
            </div>
          </div>
          <DialogFooter className={isRTL ? 'flex-row-reverse' : ''}>
            <Button variant="outline" onClick={() => setShowDialog(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleSave}>{t('pages.common.add')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Attendance;
